// src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import InfoPage from './pages/InfoPage';
import DashboardPage from './pages/DashboardPage';
import InventoryPage from './pages/InventoryPage';
import ProductsPage from './pages/ProductsPage';
import WarehousesPage from './pages/WarehousesPage';
import CompaniesPage from './pages/CompaniesPage';
import CertificatesPage from './pages/CertificatesPage';
import AlertsPage from './pages/AlertsPage';
import AIChatPage from './pages/AIChatPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import AdsPage from './pages/AdsPage';
import StaffPage from './pages/StaffPage';
import AttendancePage from './pages/AttendancePage';
import BillingPage from './pages/BillingPage';
import AdminBillingPage from './pages/AdminBillingPage';
import NotFoundPage from './pages/NotFoundPage';

// Layout
import AppLayout from './components/layout/AppLayout';

// ── Route guards ──────────────────────────────────────────────────────────────

function LoadingScreen() {
    return (
        <div style={{
            minHeight: '100vh', background: 'var(--bg-base)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <div style={{
                width: 32, height: 32,
                border: '2px solid rgba(74,158,110,0.2)',
                borderTopColor: 'var(--green-400)',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

function RequireAuth({ children, roles }) {
    const { user, loading } = useAuth();
    if (loading) return <LoadingScreen />;
    if (!user) return <Navigate to="/login" replace />;
    if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
    return children;
}

function RequireGuest({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <LoadingScreen />;
    if (user) return <Navigate to="/" replace />;
    return children;
}

// ── Simple success / cancelled pages for Stripe redirect ─────────────────────

function BillingSuccess() {
    return (
        <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 48 }}>🎉</div>
            <h1 style={{ color: 'var(--text-primary)', margin: 0 }}>Payment Successful</h1>
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Your subscription is now active. It may take a few seconds to reflect.</p>
            <a href="/billing" style={{ color: 'var(--green-300)', marginTop: 8 }}>← Back to Billing</a>
        </div>
    );
}

function BillingCancelled() {
    return (
        <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 48 }}>—</div>
            <h1 style={{ color: 'var(--text-primary)', margin: 0 }}>Checkout Cancelled</h1>
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>No charges were made. You can subscribe whenever you are ready.</p>
            <a href="/billing" style={{ color: 'var(--green-300)', marginTop: 8 }}>← Back to Billing</a>
        </div>
    );
}

// ── Router ────────────────────────────────────────────────────────────────────

function AppRoutes() {
    return (
        <Routes>
            {/* Public */}
            <Route path="/login" element={<RequireGuest><LoginPage /></RequireGuest>} />

            {/* Public info / marketing page — open to anyone with the URL, no auth */}
            <Route path="/info" element={<InfoPage />} />

            {/* Stripe redirect pages — outside AppLayout so no sidebar */}
            <Route path="/billing/success" element={<RequireAuth><BillingSuccess /></RequireAuth>} />
            <Route path="/billing/cancelled" element={<RequireAuth><BillingCancelled /></RequireAuth>} />

            {/* Protected — all authenticated users */}
            <Route path="/" element={<RequireAuth><AppLayout /></RequireAuth>}>
                <Route index element={<DashboardPage />} />
                <Route path="inventory" element={<InventoryPage />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="warehouses" element={<WarehousesPage />} />
                <Route path="alerts" element={<AlertsPage />} />
                <Route path="ai-chat" element={<AIChatPage />} />
                <Route path="knowledge-base" element={<KnowledgeBasePage />} />
                <Route path="staff" element={<StaffPage />} />
                <Route path="attendance" element={<AttendancePage />} />
                <Route path="certificates" element={<CertificatesPage />} />
                <Route path="billing" element={<BillingPage />} />

                {/* GAAF Admin only */}
                <Route path="companies" element={
                    <RequireAuth roles={['gaaf_admin']}><CompaniesPage /></RequireAuth>
                } />
                <Route path="ads" element={
                    <RequireAuth roles={['gaaf_admin']}><AdsPage /></RequireAuth>
                } />
                <Route path="billing/admin" element={
                    <RequireAuth roles={['gaaf_admin']}><AdminBillingPage /></RequireAuth>
                } />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
        </Routes>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AppRoutes />
                <Toaster
                    position="top-right"
                    toastOptions={{
                        style: {
                            background: 'var(--bg-elevated)',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border-default)',
                            fontFamily: 'var(--font-body)',
                            fontSize: '0.875rem',
                        },
                        success: { iconTheme: { primary: 'var(--green-400)', secondary: 'var(--bg-elevated)' } },
                        error: { iconTheme: { primary: '#e07a7a', secondary: 'var(--bg-elevated)' } },
                    }}
                />
            </BrowserRouter>
        </AuthProvider>
    );
}
